// Polyfill SharedArrayBuffer for non-secure contexts (HTTP, Tailscale networks, etc.)
// SharedArrayBuffer requires HTTPS or localhost, so we fall back to ArrayBuffer
if (typeof SharedArrayBuffer === 'undefined') {
  // @ts-expect-error - Polyfill for environments where SharedArrayBuffer is not available
  globalThis.SharedArrayBuffer = ArrayBuffer
}

import * as Sentry from '@sentry/vue'
import { printWelcome } from '@/shared/utils/welcome'
import { useAccessControlService } from '@A/services/access-control.service'
import { useAppVersion } from '@/shared/composables/useAppVersion'
import { useKeycloak } from '@/shared/composables/useKeycloak'
import { useStoreApp } from '@/stores/app'
import { useOrganizationStore } from '@/stores/organization'
import { useApi } from '@/shared/composables/useApi'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { router } from '@/router'
import { vTooltip } from '@/shared/directives/tooltip'

import App from '@/App.vue'

import '@/input.css'

// Console welcome message
printWelcome(__APP_VERSION__)

// Set favicon based on site mode
const siteMode = import.meta.env.VITE_SITE_MODE
if (siteMode === 'dev') {
  const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
  if (link) {
    link.href = '/favicon-dev.ico'
  }
  else {
    const newLink = document.createElement('link')
    newLink.rel = 'icon'
    newLink.href = '/favicon-dev.ico'
    document.head.appendChild(newLink)
  }
}

// Get Keycloak composable
const { initKeycloak, loadSessionTimeoutsFromBackend } = useKeycloak()

// Create app and pinia
const pinia = createPinia()
const app = createApp(App)

// Initialize Sentry as early as possible after the app is created. Disabled
// (no-op) when VITE_SENTRY_DSN is unset, so local dev is unaffected.
Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  // Decoupled from VITE_MODE so the Sentry environment (local/dev/prod) is
  // explicit and not tied to the app's build mode.
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'local',
  release: __APP_VERSION__,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    // Strict masking: replays capture layout but never text or media content,
    // protecting the org/user/aquaculture data shown on screen.
    Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    // Forward handled errors logged with console.error to Sentry. The Vue
    // integration only captures *unhandled* errors; this covers the errors that
    // are caught and logged (e.g. the central handleApiError handler).
    Sentry.captureConsoleIntegration({ levels: ['error'] })
  ],
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
  // Connect frontend traces to backend traces by propagating tracing headers
  // to the API host. CORS already allows credentialed requests to the API.
  // Same-origin setups (containerized, or the Vite dev proxy: relative /api/v1
  // URLs) must match only relative URLs — an empty-string target would
  // substring-match EVERY url, attaching sentry-trace/baggage headers to
  // Keycloak requests and breaking its CORS preflight.
  tracePropagationTargets:
    import.meta.env.VITE_API_PROXY !== 'true' && import.meta.env.VITE_APIHOST
      ? [import.meta.env.VITE_APIHOST]
      : [/^\//],
  // Session Replay: sample a fraction of all sessions, but always capture
  // sessions where an error occurs.
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
})

// Use Pinia first - required for router guards to access stores
app.use(pinia)

app.directive('tooltip', vTooltip)

// Determine Keycloak mode based on route
// Public routes like accept-invitation should use 'check-sso' to allow unauthenticated access
const isPublicRoute = window.location.pathname === '/accept-invitation'
const keycloakMode = isPublicRoute ? 'check-sso' : 'login-required'

// Initialize Keycloak and mount app after authentication
initKeycloak({ onLoad: keycloakMode })
  .then(async (authenticated) => {
    // Initialize API configuration first
    const appStore = useStoreApp()
    appStore.initializeApiConfig()

    // Set API base URL for useApi composable
    const { setApiBaseUrl } = useApi()
    setApiBaseUrl(appStore.baseApiUrl)

    // Fetch realm session timeouts so the "Daily session limit reached"
    // detection uses the actual realm config instead of the hardcoded default.
    // Fire-and-forget — silent fallback to default on failure.
    void loadSessionTimeoutsFromBackend(appStore.baseApiUrl)

    // Only initialize permissions and organizations if user is authenticated
    if (authenticated) {
      // Initialize user permissions (also populates the org list — single endpoint)
      const accessControlService = useAccessControlService()
      await accessControlService.initializeCurrentUserPermissions()

      // Restore current organization from session, if any
      const organizationStore = useOrganizationStore()
      organizationStore.initializeFromSession()

      // Open the deploy auto-reload subscription now that we have a valid token.
      // Decoupled from AppVersionReloadDialog (which is purely presentational)
      // so the version watch survives any change to how the dialog is rendered.
      useAppVersion().init()
    }

    // The router read the URL when it was created, before Keycloak removed its login callback
    // from the hash. Start it on the cleaned URL; app.use then skips its own first navigation,
    // which would write the stale callback back.
    // A failure only warns, like vue-router's own first navigation, so the app still mounts.
    const { pathname, search, hash } = window.location
    await router.replace(pathname + search + hash).catch((err) => console.warn('Failed to start the router:', err))

    // Now set up the rest of the app
    app.use(router)

    // Mount the app
    app.mount('#app')
  })
  .catch((error) => {
    console.error('Failed to initialize app:', error)
    Sentry.captureException(error)
  })
